import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const FREE_GIFT_VARIANT_ID = window.freeGiftVariantId;
const FREE_GIFT_HANDLE = 'bleuface-free-gift-foc';

let syncing = false;
let syncQueued = false;

async function getCart() {
  const response = await fetch('/cart.js', {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch cart: ${response.status}`);
  }

  return response.json();
}

async function updateGift(variantId, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: variantId,
      quantity,
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update gift: ${response.status}`);
  }

  return response.json();
}

async function addGift(variantId) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to add free gift: ${response.status}`);
  }

  return response.json();
}

async function syncFreeGift() {
  if (!FREE_GIFT_VARIANT_ID) {
    console.error('[Free Gift] Gift variant ID is missing.');
    return;
  }

  if (syncing) {
    syncQueued = true;
    return;
  }

  syncing = true;

  try {
    const cart = await getCart();

    const giftItems = cart.items.filter(
      (item) => item.variant_id === Number(FREE_GIFT_VARIANT_ID)
    );

    const regularItems = cart.items.filter(
      (item) => item.variant_id !== Number(FREE_GIFT_VARIANT_ID)
    );

    /*
     * No normal products in cart.
     * Remove all gift lines.
     */
    if (regularItems.length === 0) {
      for (const giftItem of giftItems) {
        const updatedCart = await updateGift(giftItem.key, 0);

        document.dispatchEvent(
          new CartUpdateEvent(updatedCart, 'free-gift', {
            source: 'free-gift',
            itemCount: updatedCart.item_count,
            sections: updatedCart.sections,
          })
        );
      }

      return;
    }

    /*
     * A normal product exists.
     *
     * If gift already exists, make sure exactly one
     * remains in the cart.
     */
    if (giftItems.length > 0) {
      const primaryGift = giftItems[0];

      if (primaryGift.quantity !== 1) {
        const updatedCart = await updateGift(primaryGift.key, 1);

        document.dispatchEvent(
          new CartUpdateEvent(updatedCart, 'free-gift', {
            source: 'free-gift',
            itemCount: updatedCart.item_count,
            sections: updatedCart.sections,
          })
        );
      }

      /*
       * Remove duplicate gift lines if they somehow exist.
       */
      for (const duplicateGift of giftItems.slice(1)) {
        const updatedCart = await updateGift(duplicateGift.key, 0);

        document.dispatchEvent(
          new CartUpdateEvent(updatedCart, 'free-gift', {
            source: 'free-gift',
            itemCount: updatedCart.item_count,
            sections: updatedCart.sections,
          })
        );
      }

      return;
    }

    /*
     * Normal product exists but gift doesn't.
     * Add exactly one gift.
     */
    const updatedCart = await addGift(FREE_GIFT_VARIANT_ID);

    document.dispatchEvent(
      new CartUpdateEvent(updatedCart, 'free-gift', {
        source: 'free-gift',
        variantId: String(FREE_GIFT_VARIANT_ID),
        itemCount: updatedCart.item_count,
        sections: updatedCart.sections,
      })
    );
  } catch (error) {
    console.error('[Free Gift]', error);
  } finally {
    syncing = false;

    if (syncQueued) {
      syncQueued = false;
      syncFreeGift();
    }
  }
}

/*
 * Run once when the page loads.
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncFreeGift, { once: true });
} else {
  syncFreeGift();
}

/*
 * Run whenever Horizon updates the cart.
 */
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  /*
   * Ignore our own event.
   * Otherwise adding the gift would trigger another sync cycle.
   */
  if (event.detail?.data?.source === 'free-gift') {
    return;
  }

  syncFreeGift();
});